// Scrollable message list + state cards. Receives all state as props.
//
// Iter33: greeting is rendered as a STANDALONE bubble (never in the messages
// array). When messages.length === 0, a strip of quick-start chips
// (4 static + up to 2 situational from /api/briefing) is shown. The chips
// disappear the moment the first user message lands.
import React from "react";
import { ArrowRight } from "lucide-react";
import { ChatMessageBubble, ChatTypingIndicator } from "./ChatMessageBubble";
import { ChatSuggestionCard } from "./ChatSuggestionCard";
import { ChatReadyCard, ChatCreatedCard } from "./ChatStateCards";
import { AI_CHAT_STATIC_CHIPS, GREETING } from "./aiChatConstants";

const ChipButton = ({ icon, label, onClick, testid, accent }) => (
  <button
    onClick={onClick}
    className="px-3 py-1.5 rounded-full transition-colors"
    style={{
      background: accent
        ? "rgba(226,75,74,0.10)"
        : "rgba(155,89,182,0.08)",
      border: accent
        ? "1px solid rgba(226,75,74,0.45)"
        : "1px solid rgba(155,89,182,0.30)",
      color: accent ? "#ff9a99" : "#c89adb",
      fontFamily: "'DM Mono', monospace",
      fontSize: 11,
      cursor: "pointer",
      letterSpacing: "0.3px",
    }}
    data-testid={testid}
    data-chip-label={label}
  >
    <span style={{ marginRight: 6 }}>{icon}</span>{label}
  </button>
);

const buildSituationalChips = (briefing) => {
  if (!briefing) return [];
  const out = [];
  const s1n = (briefing.s1_incidents || []).length;
  const breachn = briefing.sla_breaches?.count || 0;
  if (s1n > 0) {
    out.push({
      icon: "🔴",
      label: "Aktív S1 — folytatás",
      text: `Folytatom egy aktív S1 incidenst (${s1n} db nyitva).`,
    });
  }
  if (breachn > 0) {
    out.push({
      icon: "🚨",
      label: "SLA breach — új incidens",
      text: `Új incidens — egy másik már SLA breach-ben van (${breachn} db).`,
    });
  }
  return out.slice(0, 2);
};

export const ChatMessageList = ({
  scrollRef,
  messages,
  busy,
  createdIncident,
  readyToCreate,
  extracted,
  activeSuggestion,
  hitTurnLimit,
  briefing,
  onAcceptSuggestion,
  onSkipSuggestion,
  onConfirmCreate,
  onEditInWizard,
  onOpenCreatedIncident,
  onSwitchToWizard,
  onChipClick,
}) => {
  // Chips visibility is the single invariant: empty messages array → show.
  const chipsVisible = messages.length === 0 && !busy && !createdIncident;
  const situational = buildSituationalChips(briefing);
  return (
    <div
      ref={scrollRef}
      className="flex-1 overflow-y-auto px-4 sm:px-8 py-6 space-y-4"
      data-testid="ai-chat-messages"
    >
      {/* Greeting bubble — rendered independently of `messages` */}
      <div data-testid="ai-chat-greeting">
        <ChatMessageBubble
          message={{ role: "assistant", content: "__greeting__", display: GREETING }}
        />
      </div>

      {messages.map((m, i) => (
        <ChatMessageBubble key={i} message={m} />
      ))}
      {busy && <ChatTypingIndicator />}

      {chipsVisible && (
        <div className="space-y-2 pt-1" data-testid="ai-chat-chips-container">
          {situational.length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid="ai-chat-chips-situational">
              {situational.map((c, i) => (
                <ChipButton
                  key={`sit-${i}`}
                  icon={c.icon}
                  label={c.label}
                  onClick={() => onChipClick && onChipClick(c.text)}
                  testid="ai-chat-chip-situational"
                  accent
                />
              ))}
            </div>
          )}
          <div className="flex flex-wrap gap-2" data-testid="ai-chat-chips-static">
            {AI_CHAT_STATIC_CHIPS.map((c, i) => (
              <ChipButton
                key={`stat-${i}`}
                icon={c.icon}
                label={c.label}
                onClick={() => onChipClick && onChipClick(c.text)}
                testid="ai-chat-chip-static"
              />
            ))}
          </div>
        </div>
      )}

      {activeSuggestion && !busy && !createdIncident && (
        <ChatSuggestionCard
          text={activeSuggestion}
          onAccept={onAcceptSuggestion}
          onSkip={onSkipSuggestion}
        />
      )}

      {readyToCreate && !createdIncident && !busy && (
        <ChatReadyCard
          extracted={extracted}
          onCreate={onConfirmCreate}
          onEdit={onEditInWizard}
        />
      )}

      {createdIncident && (
        <ChatCreatedCard incident={createdIncident} onOpen={onOpenCreatedIncident} />
      )}

      {hitTurnLimit && (
        <div
          className="rounded-md px-4 py-3"
          style={{
            background: "rgba(243,177,74,0.10)",
            border: "1px solid rgba(243,177,74,0.35)",
            fontFamily: "'DM Mono', monospace",
            fontSize: 12,
          }}
          data-testid="ai-chat-turn-limit"
        >
          <div style={{ color: "#f3b14a", marginBottom: 6 }}>
            We've covered a lot — let's finish in the wizard with what we have.
          </div>
          <button
            onClick={onSwitchToWizard}
            className="inline-flex items-center gap-2 px-3 py-2 rounded"
            style={{
              background: "#185FA5",
              color: "#fff",
              fontFamily: "Syne, sans-serif",
              fontWeight: 600,
              fontSize: 12,
              border: "1px solid #185FA5",
              cursor: "pointer",
            }}
            data-testid="ai-chat-switch-wizard"
          >
            Switch to wizard <ArrowRight size={13} />
          </button>
        </div>
      )}
    </div>
  );
};
